import { onboardingCourseProgress } from '@chiroup/core/functions/onboarding';
import { OnboardingView } from '@chiroup/core/types/Onboarding.type';
import { ChevronDown, GraduationCap, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Section, Status, statusLabel } from './OnboardingUi';

const OnboardingTrainingMatrix = ({ view }: { view: OnboardingView }) => {
  const [showHistory, setShowHistory] = useState(false);
  const courses = view.content.courses.filter(
    course => showHistory || (course.active && !course.retired)
  );
  return (
    <Section
      title="Team Training Verification"
      eyebrow="ChiroUp Academy"
      tone="sky"
      icon={<GraduationCap className="h-5 w-5" />}
      description="Review each person’s self-reported progress. Training is read-only here and is separate from clinic setup completion."
    >
      <details className="group/matrix">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-xl border border-sky-500/15 bg-sky-50/45 p-4 text-base font-medium text-sky-950 hover:bg-sky-100/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-700 dark:bg-sky-950/25 dark:text-sky-100 dark:hover:bg-sky-900/30 sm:text-sm">
          <div className="flex items-center gap-3">
            <UsersRound
              className="h-5 w-5 shrink-0 text-sky-700 dark:text-sky-300"
              aria-hidden="true"
            />
            <div>
              View course completion · {view.team.filter(member => member.active).length} active
              users
              <p className="mt-1 font-normal text-sky-800 dark:text-sky-200">
                Expand to review each person’s courses and topics.
              </p>
            </div>
          </div>
          <ChevronDown
            className="h-5 w-5 shrink-0 motion-safe:transition-transform group-open/matrix:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <label className="mt-4 flex items-center gap-2 text-base sm:text-sm">
          <input
            type="checkbox"
            name="show-training-history"
            className="h-5 w-5 accent-primary-600"
            checked={showHistory}
            onChange={event => setShowHistory(event.target.checked)}
          />
          Include inactive and retired training history
        </label>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-left text-base sm:text-sm">
            <caption className="sr-only">Read-only user training completion by course</caption>
            <thead className="border-b border-border bg-muted/45 text-muted-foreground">
              <tr>
                <th scope="col" className="sticky left-0 z-10 min-w-48 bg-card p-4 font-medium">
                  Clinic user
                </th>
                {courses.map(course => (
                  <th scope="col" key={course.id} className="min-w-44 p-4 font-medium">
                    {course.title}
                    {(!course.active || course.retired) && (
                      <p className="font-normal">Historical course</p>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {view.team.map(member => (
                <tr key={member.id} className={member.active ? '' : 'bg-muted/40'}>
                  <th scope="row" className="sticky left-0 z-10 bg-card p-4 font-medium">
                    {member.name}
                    {!member.active && (
                      <p className="font-normal text-muted-foreground">
                        Inactive · history retained
                      </p>
                    )}
                  </th>
                  {courses.map(course => {
                    const progress = onboardingCourseProgress(course, member.training[course.id]);
                    return (
                      <td key={course.id} className="p-3 align-top">
                        <details className="group/course">
                          <summary className="cursor-pointer list-none rounded-xl p-2 tabular-nums hover:bg-muted/45 focus-visible:outline-2 focus-visible:outline-sky-700 dark:focus-visible:outline-sky-400">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-semibold">
                                {progress.status === 'not_applicable'
                                  ? 'N/A'
                                  : `${progress.percent}%`}
                              </p>
                              <ChevronDown
                                className="h-4 w-4 text-muted-foreground motion-safe:transition-transform group-open/course:rotate-180"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="mt-1">
                              <Status value={progress.status} />
                            </div>
                          </summary>
                          <ul role="list" className="mt-3 space-y-2">
                            {course.topics
                              .filter(topic => showHistory || (topic.active && !topic.retired))
                              .map(topic => (
                                <li
                                  key={topic.id}
                                  className="space-y-1.5 border-t border-border pt-3"
                                >
                                  <p>
                                    {topic.title}
                                    {(!topic.active || topic.retired) && ' · Historical topic'}
                                  </p>
                                  <p className="text-sky-800 dark:text-sky-200">
                                    {statusLabel(
                                      member.training[course.id]?.dismissed
                                        ? 'not_applicable'
                                        : member.training[course.id]?.topics[topic.id] ||
                                            'not_started'
                                    )}
                                  </p>
                                </li>
                              ))}
                          </ul>
                        </details>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {!view.team.length && (
            <div className="flex flex-col items-center gap-3 px-5 py-8 text-center">
              <UsersRound className="h-7 w-7 text-sky-700 dark:text-sky-300" aria-hidden="true" />
              <p className="font-medium">No eligible clinic users yet.</p>
              <p className="max-w-[45ch] text-base text-muted-foreground sm:text-sm">
                Team members will appear here with their own course progress.
              </p>
            </div>
          )}
        </div>
      </details>
    </Section>
  );
};

export default OnboardingTrainingMatrix;
